import type { LearnFAQ } from "@/pages/learn/learnShared";
import {
  makeEsArticle,
  esSaintRelated,
  type EsArticleInput,
} from "./learnFactoryEs";

const reviewBlock = {
  writtenBy: "Neuro Wellness TMS Centers of America",
  reviewedBy: "Daniel Pietras, MD",
  published: "November 3, 2026",
  lastReviewed: "November 3, 2026",
  lastUpdated: "November 3, 2026",
};

const cole2020 = {
  citation:
    "Cole EJ, et al. Stanford Accelerated Intelligent Neuromodulation Therapy for Treatment-Resistant Depression. Am J Psychiatry. 2020;177(8):716-726.",
  url: "https://doi.org/10.1176/appi.ajp.2019.19070720",
};
const cole2022 = {
  citation:
    "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): A Double-Blind Randomized Controlled Trial. Am J Psychiatry. 2022;179(2):132-141.",
  url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
};

const faqs: LearnFAQ[] = [
  {
    question: "¿Cuál es la diferencia entre SAINT® y la ketamina?",
    answer:
      "SAINT® es estimulación magnética transcraneal administrada durante cinco días con orientación por fMRI. La ketamina es un anestésico administrado por infusión intravenosa (uso no aprobado para depresión) o como esketamina nasal (Spravato, autorizado por la FDA). Actúan por mecanismos distintos.",
  },
  {
    question: "¿SAINT® o la ketamina es mejor para la depresión?",
    answer:
      "No hay una respuesta universal. La idoneidad depende del diagnóstico, el historial de tratamiento y otros factores. Ambos requieren una evaluación psiquiátrica para determinar la elegibilidad.",
  },
];

export const esSaintVsKetamine = makeEsArticle({
  slug: "saint-vs-ketamine",
  title: "SAINT® vs ketamina para la depresión",
  description:
    "SAINT® TMS frente a la ketamina comparados: mecanismo, calendario, orientación, entorno de administración, estado de aprobación y quién puede ser evaluado. La idoneidad depende de las circunstancias.",
  h1: "SAINT® vs ketamina: ¿cuál es la diferencia?",
  breadcrumbLabel: "SAINT® vs ketamina",
  shortAnswer:
    "SAINT® y la ketamina son tratamientos distintos para la depresión. SAINT® es estimulación magnética transcraneal acelerada administrada durante cinco días con orientación por fMRI. La ketamina es un anestésico administrado por infusión intravenosa (uso no aprobado para depresión) o como esketamina nasal (Spravato, autorizado por la FDA). Ambos requieren una evaluación psiquiátrica para confirmar la elegibilidad. La idoneidad depende de las circunstancias individuales.",
  keyTakeaways: [
    "SAINT® es TMS acelerada con orientación por fMRI durante cinco días.",
    "La ketamina IV es un anestésico administrado por infusión (uso no aprobado para depresión).",
    "Spravato (esketamina nasal) está autorizado por la FDA para la depresión resistente al tratamiento.",
    "Ambos requieren evaluación psiquiátrica para confirmar la elegibilidad.",
    "La idoneidad depende de las circunstancias individuales.",
  ],
  sections: [
    {
      heading: "Mecanismo de acción",
      paragraphs: [
        "SAINT® utiliza estimulación magnética transcraneal theta burst para modular la actividad del circuito cerebral asociado con la depresión, con orientación individualizada por fMRI.",
        "La ketamina actúa principalmente como antagonista del receptor NMDA. Las infusiones de ketamina para la depresión son un uso no aprobado por la FDA (off-label). Spravato (esketamina nasal) está autorizado por la FDA para la depresión resistente al tratamiento.",
      ],
    },
    {
      heading: "Calendario y administración",
      paragraphs: [
        "SAINT® se administra durante cinco días consecutivos con diez sesiones por día. El paciente permanece despierto durante cada sesión.",
        "Las infusiones de ketamina se administran por vía intravenosa durante aproximadamente 40 minutos, en una serie de sesiones a lo largo de varias semanas. Spravato se administra en spray nasal en una clínica certificada con monitorización posterior.",
      ],
    },
    {
      heading: "¿Cuál podría ser adecuado para usted?",
      paragraphs: [
        "No hay una respuesta universal. La idoneidad de SAINT®, de la ketamina o de Spravato depende del diagnóstico, el historial de tratamiento y otros factores. Una evaluación psiquiátrica es el primer paso.",
        "[[Solicite una consulta|/es/saint]] o llame al 954-827-9793.",
      ],
    },
  ],
  faqs,
  sources: [cole2020, cole2022],
  relatedLinks: esSaintRelated,
  reviewBlock,
} satisfies EsArticleInput);
